import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
  FormControl, Select, MenuItem, Tooltip, IconButton, Snackbar, Alert, TextField, Switch, Menu, Chip, Avatar,
  Skeleton
} from '@mui/material';
import { Edit, X, Settings } from 'lucide-react';
import AchatSelector from './AchatSelector';
import api from '../utils/api';
import currencyService from '../services/currencyService';
import { extractAmazonFees } from '../utils/amazonFees';
import computeFinalPrice, { getTVADetails, getProfit, getMargin, getROI, computeFinalPriceForMargin } from '../utils/calculs';

// Mapping des taux de TVA par pays
const TVA_BY_COUNTRY = {
  FR: 20,
  DE: 19,
  IT: 22,
  ES: 21,
  UK: 20,
  NL: 21,
  BE: 21,
  SE: 25,
  PL: 23,
  IE: 23
};

// Ajout de la constante MARKETPLACES
const MARKETPLACES = [
  { id: 'A13V1IB3VIYZZH', code: 'FR', label: 'France', flag: 'https://flagcdn.com/24x18/fr.png', currency: '€', currencyCode: 'EUR' },
  { id: 'APJ6JRA9NG5V4', code: 'IT', label: 'Italie', flag: 'https://flagcdn.com/24x18/it.png', currency: '€', currencyCode: 'EUR' },
  { id: 'A1RKKUPIHCS9HS', code: 'ES', label: 'Espagne', flag: 'https://flagcdn.com/24x18/es.png', currency: '€', currencyCode: 'EUR' },
  { id: 'A1805IZSGTT6HS', code: 'NL', label: 'Pays-Bas', flag: 'https://flagcdn.com/24x18/nl.png', currency: '€', currencyCode: 'EUR' },
  { id: 'A2NODRKZP88ZB9', code: 'SE', label: 'Suède', flag: 'https://flagcdn.com/24x18/se.png', currency: 'kr', currencyCode: 'SEK' },
  { id: 'A1C3SOZRARQ6R3', code: 'PL', label: 'Pologne', flag: 'https://flagcdn.com/24x18/pl.png', currency: 'zł', currencyCode: 'PLN' },
  { id: 'AMEN7PMS3EDWL', code: 'BE', label: 'Belgique', flag: 'https://flagcdn.com/24x18/be.png', currency: '€', currencyCode: 'EUR' },
  { id: 'A1PA6795UKMFR9', code: 'DE', label: 'Allemagne', flag: 'https://flagcdn.com/24x18/de.png', currency: '€', currencyCode: 'EUR' },
  { id: 'A1F83G8C2ARO7P', code: 'UK', label: 'Royaume-Uni', flag: 'https://flagcdn.com/24x18/gb.png', currency: '£', currencyCode: 'GBP' },
];

// Ajout de la constante des marketplaces étrangères
const foreigns = ['SEK', 'PLN', 'GBP'];

const DialogRepricer = ({
  open,
  onClose,
  selectedProduct,
  repricingRuleData,
  setRepricingRuleData,
  showSnackbar,
  onRulesUpdated,
  restrictionsByProduct = {},
}) => {
  // Handlers internes pour les menus et overrides
  const [anchorEl, setAnchorEl] = useState(null);
  const [editRuleCountry, setEditRuleCountry] = useState(null);
  const [overrideDialog, setOverrideDialog] = useState({ open: false, country: null });
  const [overrideForm, setOverrideForm] = useState({ minProfit: '', minMargin: '', minROI: '', maxProfit: '', maxMargin: '', maxROI: '', price: '' });

  // Ajout de tous les hooks et états nécessaires pour rendre la modale autonome
  const [rules, setRules] = useState([]);
  const [achatsProduit, setAchatsProduit] = useState([]);
  const [selectedAchatId, setSelectedAchatId] = useState('');
  const [openPersonnalisation, setOpenPersonnalisation] = useState({});
  const [personnalisation, setPersonnalisation] = useState({});
  const [feesByMp, setFeesByMp] = useState({});
  const [feesDetailsByMp, setFeesDetailsByMp] = useState({});
  const [customFeesByMp, setCustomFeesByMp] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [exchangeRates, setExchangeRates] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedRuleIds, setSelectedRuleIds] = useState({});
  const [repricerActif, setRepricerActif] = useState({});
  const [amazonPrices, setAmazonPrices] = useState({});
  const [repricerActifBdd, setRepricerActifBdd] = useState({});
  const [reactivatedThisSession, setReactivatedThisSession] = useState({});

  // Mémorise l'état précédent du toggle pour chaque marketplace
  const prevRepricerActifRef = useRef({});
  useEffect(() => {
    if (!selectedProduct) return;
    const prev = prevRepricerActifRef.current[selectedProduct.id] || {};
    MARKETPLACES.forEach(mp => {
      const wasActive = !!prev[mp.code];
      const isActive = !!repricerActif[selectedProduct.id]?.[mp.code];
      const hasTargetPrice = repricingRuleData[selectedProduct.id]?.[mp.code]?.target_price !== undefined && repricingRuleData[selectedProduct.id]?.[mp.code]?.target_price !== null;
      // Si on vient de passer de OFF à ON ET qu'il n'y a pas de target_price en BDD
      if (!wasActive && isActive && !hasTargetPrice) {
        // Marque comme réactivée dans la session
        setReactivatedThisSession(prevState => ({
          ...prevState,
          [mp.code]: true
        }));
        // Rafraîchir le prix Amazon
        if (selectedProduct.ASIN) {
          // Récupérer le SKUID de l'achat sélectionné
          const achat = getAchatUtilise(achatsProduit, selectedAchatId);
          const sku = achat?.SKUID || '';
          fetch('/api/amazon/my-price', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ asin: selectedProduct.ASIN, marketplaceId: mp.id, sku })
          })
            .then(res => res.json())
            .then(data => {
              if (data && data.price) {
                setAmazonPrices(prev => ({ ...prev, [mp.code]: data.price }));
                // Rafraîchir les frais Amazon
                fetch('/api/amazon/estimate-fees', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    asin: selectedProduct.ASIN,
                    marketplaceId: mp.id,
                    price: Number(data.price),
                    currency: mp.currencyCode
                  })
                })
                  .then(res => res.json())
                  .then(feesData => {
                    const feesExtracted = extractAmazonFees(feesData.FeesEstimate || feesData || {});
                    setFeesByMp(prev => ({ ...prev, [mp.code]: feesExtracted.Total }));
                    setFeesDetailsByMp(prev => ({ ...prev, [mp.code]: feesExtracted }));
                    setCustomFeesByMp(prev => ({ ...prev, [mp.code]: feesExtracted }));
                  })
                  .catch(() => {
                    setFeesByMp(prev => ({ ...prev, [mp.code]: 0 }));
                    setFeesDetailsByMp(prev => ({ ...prev, [mp.code]: {} }));
                    setCustomFeesByMp(prev => ({ ...prev, [mp.code]: {} }));
                  });
              } else {
                setAmazonPrices(prev => ({ ...prev, [mp.code]: null }));
              }
            })
            .catch(() => {
              setAmazonPrices(prev => ({ ...prev, [mp.code]: null }));
            });
        }
      }
    });
    // Met à jour la référence pour la prochaine fois
    prevRepricerActifRef.current[selectedProduct.id] = { ...repricerActif[selectedProduct.id] };
    // eslint-disable-next-line
  }, [repricerActif, selectedProduct, repricingRuleData]);

  // Réinitialise reactivatedThisSession et amazonPrices à chaque ouverture/fermeture ou changement de produit
  useEffect(() => {
    setReactivatedThisSession({});
    setAmazonPrices({});
  }, [open, selectedProduct]);

  // Chargement des taux de change (SEK, PLN, GBP)
  useEffect(() => {
    async function loadRates() {
      const rates = {};
      for (const currency of ['SEK', 'PLN', 'GBP']) {
        try {
          const rate = await currencyService.getExchangeRate(currency, 'EUR');
          rates[currency] = rate;
        } catch (e) {
          rates[currency] = null;
        }
      }
      setExchangeRates(rates);
    }
    loadRates();
  }, []);

  // Chargement des règles à l'ouverture
  useEffect(() => {
    if (open) {
      fetch('/api/rules')
        .then(res => res.json())
        .then(data => setRules(data))
        .catch(() => setRules([]));
    }
  }, [open]);

  // Chargement des achats du produit sélectionné
  useEffect(() => {
    if (selectedProduct?.id) {
      fetch(`/api/achats?produit=${selectedProduct.id}`)
        .then(res => res.json())
        .then(data => {
          setAchatsProduit(data);
          if (data.length > 0) {
            // Cherche l'achat_id de la règle
            let achatIdFromRule = null;
            const rulesForProduct = repricingRuleData[selectedProduct.id] || {};
            Object.entries(rulesForProduct).forEach(([mpCode, rule]) => {
              if (rule && rule.achat_id && data.some(a => a.id === rule.achat_id)) {
                achatIdFromRule = rule.achat_id;
              }
            });
            // Prend l'achat_id de la règle si présent et valide, sinon le plus récent
            let achatToSelect = achatIdFromRule;
            if (!achatToSelect) {
              achatToSelect = data
                .slice()
                .sort((a, b) => new Date(b.dateAchat || 0) - new Date(a.dateAchat || 0))[0]?.id;
              console.log('[DialogRepricer] Aucun achat_id de règle valide, fallback sur le plus récent:', achatToSelect);
            } else {
              console.log('[DialogRepricer] Utilisation de l\'achat_id de la règle:', achatToSelect);
            }
            setSelectedAchatId(achatToSelect || '');
          } else {
            console.warn('[DialogRepricer] Liste d\'achats vide pour le produit', selectedProduct.id);
            setSelectedAchatId('');
          }
        })
        .catch(() => {
          setAchatsProduit([]);
          setSelectedAchatId('');
          console.error('[DialogRepricer] Erreur lors du chargement des achats');
        });
    }
  }, [selectedProduct]);

  // Initialisation des règles à l'ouverture de la modale
  useEffect(() => {
    if (open && selectedProduct) {
      api.post('/api/repricing-rules/by-products', { product_ids: [selectedProduct.id] })
        .then(response => {
          const rulesArr = response.data;
          setRepricingRuleData(prev => {
            const newData = { ...prev };
            rulesArr.forEach(rule => {
              if (!newData[rule.product_id]) newData[rule.product_id] = {};
              newData[rule.product_id][rule.marketplace] = rule;
            });
            return newData;
          });
          // Initialisation des états locaux
          setSelectedRuleIds(prev => {
            const newIds = { ...prev };
            rulesArr.forEach(rule => {
              if (!newIds[rule.product_id]) newIds[rule.product_id] = {};
              newIds[rule.product_id][rule.marketplace] = rule.rule_id || '';
            });
            return newIds;
          });
          setRepricerActif(prev => {
            const newActif = { ...prev };
            rulesArr.forEach(rule => {
              if (!newActif[rule.product_id]) newActif[rule.product_id] = {};
              newActif[rule.product_id][rule.marketplace] = !!rule.active;
            });
            return newActif;
          });
          // Initialisation de l'état repricerActifBdd (snapshot à l'ouverture)
          const actifBdd = {};
          rulesArr.forEach(rule => {
            if (!actifBdd[rule.product_id]) actifBdd[rule.product_id] = {};
            actifBdd[rule.product_id][rule.marketplace] = !!rule.active;
          });
          setRepricerActifBdd(actifBdd);
        });
    }
  }, [open, selectedProduct]);

  // Initialisation de l'achat sélectionné à l'ouverture
  useEffect(() => {
    if (open && achatsProduit.length > 0) {
      // Cherche l'achat_id de la règle si existant
      let achatIdRule = null;
      const rulesForProduct = repricingRuleData[selectedProduct?.id] || {};
      Object.entries(rulesForProduct).forEach(([mpCode, rule]) => {
        if (rule && rule.achat_id) {
          achatIdRule = rule.achat_id;
        }
      });
      let selectedId = achatIdRule;
      if (!selectedId) {
        // Prendre le plus récent
        const defaultAchat = [...achatsProduit].sort((a, b) => {
          if (a.dateAchat && b.dateAchat) {
            return new Date(b.dateAchat) - new Date(a.dateAchat);
          }
          return (b.id || 0) - (a.id || 0);
        })[0];
        selectedId = defaultAchat?.id || '';
      }
      setSelectedAchatId(selectedId);
    }
  }, [open, achatsProduit, selectedProduct, repricingRuleData]);

  // 1. Ajout d'un useEffect pour initialiser les frais Amazon depuis la BDD à l'ouverture de la modale
  useEffect(() => {
    if (open && selectedProduct) {
      const rulesForProduct = repricingRuleData[selectedProduct.id] || {};
      const newFeesByMp = {};
      const newFeesDetailsByMp = {};
      Object.entries(rulesForProduct).forEach(([mpCode, rule]) => {
        if (rule && rule.active) {
          const fba = Number(rule.FBAFees || 0);
          const referral = Number(rule.ReferralFees || 0);
          const digital = Number(rule.DigitalFees || 0);
          newFeesByMp[mpCode] = fba + referral + digital;
          newFeesDetailsByMp[mpCode] = {
            FBAFees: fba,
            ReferralFees: referral,
            DigitalFees: digital,
            Total: fba + referral + digital
          };
        }
      });
      setFeesByMp(newFeesByMp);
      setFeesDetailsByMp(newFeesDetailsByMp);
    }
    // On ne fait plus d'appel estimate-fees ici !
  }, [open, selectedProduct, repricingRuleData]);

  // Initialisation de la personnalisation et ouverture des sous-lignes override à l'ouverture de la modale
  useEffect(() => {
    if (open && selectedProduct && repricingRuleData[selectedProduct.id]) {
      const rulesForProduct = repricingRuleData[selectedProduct.id];
      const newPersonnalisation = {};
      const newOpenPersonnalisation = {};
      MARKETPLACES.forEach(mp => {
        const rule = rulesForProduct[mp.code];
        const currencyCode = mp.currencyCode || mp.currency;
        const taux = exchangeRates[currencyCode];
        function toEur(val) {
          if (["SEK", "PLN", "GBP"].includes(currencyCode) && taux && val !== null && val !== undefined && !isNaN(val)) {
            return (Number(val) * taux).toFixed(2);
          }
          return val;
        }
        if (rule) {
          // Min
          if (rule.min_profit_override !== null && rule.min_profit_override !== undefined) {
            newPersonnalisation[mp.code] = { ...(newPersonnalisation[mp.code] || {}), minType: 'profit', minValue: toEur(rule.min_profit_override) };
            newOpenPersonnalisation[mp.code] = true;
          } else if (rule.min_margin_override !== null && rule.min_margin_override !== undefined) {
            newPersonnalisation[mp.code] = { ...(newPersonnalisation[mp.code] || {}), minType: 'marge', minValue: rule.min_margin_override };
            newOpenPersonnalisation[mp.code] = true;
          } else if (rule.min_roi_override !== null && rule.min_roi_override !== undefined) {
            newPersonnalisation[mp.code] = { ...(newPersonnalisation[mp.code] || {}), minType: 'roi', minValue: rule.min_roi_override };
            newOpenPersonnalisation[mp.code] = true;
          }
          // Max
          if (rule.max_profit_override !== null && rule.max_profit_override !== undefined) {
            newPersonnalisation[mp.code] = { ...(newPersonnalisation[mp.code] || {}), maxType: 'profit', maxValue: toEur(rule.max_profit_override) };
            newOpenPersonnalisation[mp.code] = true;
          } else if (rule.max_margin_override !== null && rule.max_margin_override !== undefined) {
            newPersonnalisation[mp.code] = { ...(newPersonnalisation[mp.code] || {}), maxType: 'marge', maxValue: rule.max_margin_override };
            newOpenPersonnalisation[mp.code] = true;
          } else if (rule.max_roi_override !== null && rule.max_roi_override !== undefined) {
            newPersonnalisation[mp.code] = { ...(newPersonnalisation[mp.code] || {}), maxType: 'roi', maxValue: rule.max_roi_override };
            newOpenPersonnalisation[mp.code] = true;
          }
        }
      });
      setPersonnalisation(newPersonnalisation);
      setOpenPersonnalisation(prev => ({ ...prev, ...newOpenPersonnalisation }));
    }
    // eslint-disable-next-line
  }, [open, selectedProduct, repricingRuleData, exchangeRates]);

  // Reset personnalisation et openPersonnalisation à la fermeture de la modale ou changement de produit
  useEffect(() => {
    if (!open) {
      setPersonnalisation({});
      setOpenPersonnalisation({});
    }
  }, [open, selectedProduct]);

  // Handlers pour le menu de sélection de règle
  const handleOpenRuleMenu = (event, country) => {
    setAnchorEl(event.currentTarget);
    setEditRuleCountry(country);
  };
  const handleCloseRuleMenu = () => {
    setAnchorEl(null);
    setEditRuleCountry(null);
  };
  const handleSelectRule = (rule) => {
    if (!selectedProduct || !editRuleCountry) return;
    setSelectedRuleIds(prev => ({
      ...prev,
      [selectedProduct.id]: {
        ...prev[selectedProduct.id],
        [editRuleCountry]: rule.id
      }
    }));
    handleCloseRuleMenu();
  };

  // Handlers pour l'override
  const handleOpenOverride = (country) => {
    setOverrideDialog({ open: true, country });
    setOverrideForm({ minProfit: '', minMargin: '', minROI: '', maxProfit: '', maxMargin: '', maxROI: '', price: '' });
  };
  const handleCloseOverride = () => {
    setOverrideDialog({ open: false, country: null });
  };
  const handleOverrideChange = async (field, value) => {
    setOverrideForm(prev => ({ ...prev, [field]: value }));
    // Si c'est un changement de prix, recalculer les frais
    // (À adapter si besoin, ici on ne fait rien)
  };
  const handleSaveOverride = () => {
    // TODO: enregistrer l'override
    handleCloseOverride();
  };

  // Affiche le nom de la règle sélectionnée
  const getRuleName = (produit, mp) => {
    if (!produit) return '';
    const ruleId = selectedRuleIds[produit.id]?.[mp.code];
    const rule = rules.find(r => r.id === ruleId);
    return rule ? rule.rule_name : '';
  };

  // Logique d'activation du repricer (switch Actif)
  const handleToggleRepricer = (mp) => {
    if (!selectedProduct) return;
    setRepricerActif(prev => ({
      ...prev,
      [selectedProduct.id]: {
        ...prev[selectedProduct.id],
        [mp.code]: !prev[selectedProduct.id]?.[mp.code]
      }
    }));
  };

  // Fonction de sauvegarde bulk
  const handleSaveRepricer = async () => {
    if (!selectedProduct) return;
    setSaving(true);
    try {
      // 1. Marketplaces actives (switch Actif)
      const activeMarketplaces = MARKETPLACES.filter(mp => !!repricerActif[selectedProduct.id]?.[mp.code]);
      // 2. Marketplaces qui étaient actives en BDD mais sont passées inactives
      const inactivesToDelete = Object.keys(repricingRuleData[selectedProduct.id] || {})
        .filter(mpCode => !repricerActif[selectedProduct.id]?.[mpCode])
        .map(mpCode => ({
          product_id: selectedProduct.id,
          marketplace: mpCode,
          delete: true
        }));
      // 3. Préparation des règles à sauvegarder
      const rulesToSave = activeMarketplaces.map(mp => {
        const pers = personnalisation[mp.code] || {};
        let minProfit = pers.minType === 'profit' ? pers.minValue : '';
        let maxProfit = pers.maxType === 'profit' ? pers.maxValue : '';
        // Conversion EUR -> devise locale pour SE, PL, UK
        if (["SEK", "PLN", "GBP"].includes(mp.currencyCode) && exchangeRates[mp.currencyCode]) {
          if (minProfit !== '') minProfit = (Number(minProfit) / exchangeRates[mp.currencyCode]).toFixed(2);
          if (maxProfit !== '') maxProfit = (Number(maxProfit) / exchangeRates[mp.currencyCode]).toFixed(2);
        }
        const minMargin = pers.minType === 'marge' ? pers.minValue : '';
        const minROI = pers.minType === 'roi' ? pers.minValue : '';
        const maxMargin = pers.maxType === 'marge' ? pers.maxValue : '';
        const maxROI = pers.maxType === 'roi' ? pers.maxValue : '';
        // Correction : toujours fournir un rule_id valide (sinon la règle par défaut)
        const selectedId = selectedRuleIds[selectedProduct.id]?.[mp.code];
        const fallbackId = rules.find(r => r.is_default && (r.marketplace === mp.code || !r.marketplace))?.id;
        const rule_id = rules.find(r => r.id === selectedId)?.id || fallbackId;
        // Fees
        const fees = feesDetailsByMp[mp.code] || {};
        // Correction ici : logique d'affichage identique à la colonne "Prix"
        const ruleInBdd = repricingRuleData[selectedProduct.id]?.[mp.code];
        const price =
          ruleInBdd?.target_price !== undefined && ruleInBdd?.target_price !== null
            ? Number(ruleInBdd.target_price)
            : (!!repricerActif[selectedProduct.id]?.[mp.code] && amazonPrices[mp.code] && !isNaN(amazonPrices[mp.code]))
              ? Number(amazonPrices[mp.code])
              : (foreigns.includes(mp.currencyCode) && exchangeRates[mp.currencyCode])
                ? Number(Number(selectedProduct["Prix de vente moyen"] || 0) / exchangeRates[mp.currencyCode]).toFixed(2)
                : Number(selectedProduct["Prix de vente moyen"] || 0);
        return {
          product_id: selectedProduct.id,
          marketplace: mp.code,
          rule_id,
          active: true,
          target_price: price,
          currency: repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currencyCode || 'EUR',
          achat_id: selectedAchatId || null,
          min_profit_override: minProfit !== '' ? Number(minProfit) : null,
          max_profit_override: maxProfit !== '' ? Number(maxProfit) : null,
          min_margin_override: minMargin !== '' ? Number(minMargin) : null,
          max_margin_override: maxMargin !== '' ? Number(maxMargin) : null,
          min_roi_override: minROI !== '' ? Number(minROI) : null,
          max_roi_override: maxROI !== '' ? Number(maxROI) : null,
          FBAFees: fees.FBAFees ?? null,
          ReferralFees: fees.ReferralFees ?? null,
          DigitalFees: fees.DigitalFees ?? null
        };
      });
      // 4. Fusionne sauvegardes et suppressions
      const finalPayload = [...rulesToSave, ...inactivesToDelete];
      await fetch('/api/repricing-rules/save-bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules: finalPayload })
      });
      if (showSnackbar) {
        showSnackbar({ message: 'Modifications sauvegardées !', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Modifications sauvegardées !', severity: 'success' });
      }
      // Rafraîchir les règles côté parent si demandé
      if (onRulesUpdated && selectedProduct?.id) {
        await onRulesUpdated(selectedProduct.id);
      }
      // NE PAS fermer la modale ici
    } catch (err) {
      if (showSnackbar) {
        showSnackbar({ message: 'Erreur lors de la sauvegarde', severity: 'error' });
      } else {
        setSnackbar({ open: true, message: 'Erreur lors de la sauvegarde', severity: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };


  // Fonction utilitaire pour trouver l'achat à utiliser (priorité à l'achat_id de la règle, sinon le plus récent)
  function getAchatUtilise(achatsProduit, selectedAchatId) {
    let achat = achatsProduit.find(a => a.id === selectedAchatId);
    if (!achat && achatsProduit.length > 0) {
      // Prendre le plus récent (par dateAchat si dispo, sinon id max)
      achat = [...achatsProduit].sort((a, b) => {
        if (a.dateAchat && b.dateAchat) {
          return new Date(b.dateAchat) - new Date(a.dateAchat);
        }
        return (b.id || 0) - (a.id || 0);
      })[0];
    }
    return achat || null;
  }

  // Fonction utilitaire pour convertir le prix de vente moyen EUR -> monnaie locale si besoin
  async function getDefaultPriceForMp(mp, priceEur, exchangeRates) {
    if (foreigns.includes(mp.currencyCode) && exchangeRates[mp.currencyCode]) {
      // On convertit le prix EUR vers la monnaie locale
      return (Number(priceEur) / exchangeRates[mp.currencyCode]).toFixed(2);
    }
    return Number(priceEur).toFixed(2);
  }

  // Modifie getPrice pour utiliser la conversion si pas de target_price et marketplace étrangère
  function getPrice(mp) {
    if (!selectedProduct || !mp) return '-';
    // Si target_price existe (BDD), on l'affiche
    const rule = repricingRuleData[selectedProduct.id]?.[mp.code];
    if (rule && rule.target_price !== undefined && rule.target_price !== null) {
      return Number(rule.target_price).toFixed(2);
    }
    // Sinon, si la ligne vient d'être activée dans la session et qu'on a un prix Amazon, on l'affiche
    if (reactivatedThisSession[mp.code] && amazonPrices[mp.code] && !isNaN(amazonPrices[mp.code])) {
      return Number(amazonPrices[mp.code]).toFixed(2);
    }
    // Sinon, prix de vente moyen (converti si marketplace étrangère)
    const priceEur = Number(selectedProduct["Prix de vente moyen"] || 0);
    if (foreigns.includes(mp.currencyCode) && exchangeRates[mp.currencyCode]) {
      return (priceEur / exchangeRates[mp.currencyCode]).toFixed(2);
    }
    return priceEur.toFixed(2);
  }

  // Fonction utilitaire pour affichage EUR + tooltip devise locale
  function displayEurWithTooltip(amount, currencyCode, exchangeRates) {
    if (!amount || isNaN(amount)) return '-';
    if (foreigns.includes(currencyCode) && exchangeRates[currencyCode]) {
      const eur = (Number(amount) * exchangeRates[currencyCode]).toFixed(2);
      return {
        display: `${eur} EUR`,
        tooltip: `${Number(amount).toFixed(2)} ${currencyCode}`
      };
    }
    return {
      display: `${Number(amount).toFixed(2)} ${currencyCode}`,
      tooltip: ''
    };
  }

  // Centralisation des calculs principaux par marketplace
  const computedByMp = useMemo(() => {
    const result = {};
    MARKETPLACES.forEach(mp => {
      if (!selectedProduct) return;
      let price;
      const rule = repricingRuleData[selectedProduct.id]?.[mp.code];
      if (rule && rule.target_price !== undefined && rule.target_price !== null) {
        price = Number(rule.target_price);
      } else if (reactivatedThisSession[mp.code] && amazonPrices[mp.code] && !isNaN(amazonPrices[mp.code])) {
        price = Number(amazonPrices[mp.code]);
      } else {
        price = Number(selectedProduct["Prix de vente moyen"] || 0);
      }
      const achat = getAchatUtilise(achatsProduit, selectedAchatId);
      const prixAchat = achat?.prixAchat ?? achat?.["Prix d'achat"] ?? 0;
      const fraisAmazon = Number(feesByMp[mp.code] || 0);
      const fraisPreparation = Number(achat?.frais_preparation || 0);
      const fraisLivraison = Number(achat?.frais_livraison || 0);
      const tvaDeductible = achat?.TVADeductible || false;
      const txTVA = achat?.TxTVA || 20;
      const pays = mp.code;
      const tvaDetails = getTVADetails({ prixVente: price, prixAchat, tvaDeductible, txTVA, pays });
      const profit = getProfit({ prixVente: price, prixAchat, fraisAmazon, fraisPreparation, fraisLivraison, tvaDeductible, txTVA, pays });
      const margin = getMargin({ profit, prixVente: price });
      const coutTotal = prixAchat + fraisPreparation + fraisLivraison;
      const roi = getROI({ profit, coutTotal });
      result[mp.code] = {
        tvaDetails,
        profit,
        margin,
        roi,
        price,
        prixAchat,
        fraisAmazon,
        fraisPreparation,
        fraisLivraison,
        tvaDeductible,
        txTVA,
        coutTotal,
      };
    });
    return result;
  }, [selectedProduct, achatsProduit, selectedAchatId, feesByMp, repricingRuleData, repricerActif, amazonPrices, reactivatedThisSession]);

  // Centralisation des calculs min/max (personnalisation) par marketplace
  const minMaxComputedByMp = useMemo(() => {
    const result = {};
    MARKETPLACES.forEach(mp => {
      const achat = getAchatUtilise(achatsProduit, selectedAchatId);
      let purchase = achat?.prixAchat ?? achat?.["Prix d'achat"] ?? 0;
      let prep = Number(achat?.frais_preparation || 0) + Number(achat?.frais_livraison || 0);
      let fba = feesDetailsByMp[mp.code]?.FBAFees !== undefined ? Number(feesDetailsByMp[mp.code].FBAFees) : 0;
      const vatCollectedRate = (TVA_BY_COUNTRY?.[mp.code] || 20) / 100;
      const vatDeductibleRate = achat?.TVADeductible ? (achat?.TxTVA || 20) / 100 : 0;
      // Conversion EUR -> monnaie locale pour SE, PL, UK
      if (["SEK", "PLN", "GBP"].includes(mp.currencyCode) && exchangeRates[mp.currencyCode]) {
        if (purchase) purchase = Number(purchase) / exchangeRates[mp.currencyCode];
        if (prep) prep = Number(prep) / exchangeRates[mp.currencyCode];
      }
      // Min
      const minType = personnalisation[mp.code]?.minType || 'profit';
      let minValue = personnalisation[mp.code]?.minValue || '';
      // Conversion de la valeur cible EUR -> monnaie locale pour SE, PL, UK
      if (["SEK", "PLN", "GBP"].includes(mp.currencyCode) && exchangeRates[mp.currencyCode] && minValue !== '' && !isNaN(minValue)) {
        if (minType === 'profit') {
          minValue = Number(minValue) / exchangeRates[mp.currencyCode];
        }
      }
      let minResult = null;
      if (minType && minValue !== '' && !isNaN(minValue)) {
        if (minType === 'marge') {
          minResult = computeFinalPriceForMargin({ purchase, prep, fba, marginTarget: Number(minValue) / 100, vatCollectedRate, vatDeductibleRate });
        } else {
          minResult = computeFinalPrice({ purchase, prep, fba, targetProfit: Number(minValue), vatCollectedRate, vatDeductibleRate });
        }
      } else {
        minResult = computeFinalPrice({ purchase, prep, fba, targetProfit: 0, vatCollectedRate, vatDeductibleRate });
      }
      // Max
      const maxType = personnalisation[mp.code]?.maxType || 'profit';
      let maxValue = personnalisation[mp.code]?.maxValue || '';
      if (["SEK", "PLN", "GBP"].includes(mp.currencyCode) && exchangeRates[mp.currencyCode] && maxValue !== '' && !isNaN(maxValue)) {
        if (maxType === 'profit') {
          maxValue = Number(maxValue) / exchangeRates[mp.currencyCode];
        }
      }
      let maxResult = null;
      if (maxType && maxValue !== '' && !isNaN(maxValue)) {
        if (maxType === 'marge') {
          maxResult = computeFinalPriceForMargin({ purchase, prep, fba, marginTarget: Number(maxValue) / 100, vatCollectedRate, vatDeductibleRate });
        } else {
          maxResult = computeFinalPrice({ purchase, prep, fba, targetProfit: Number(maxValue), vatCollectedRate, vatDeductibleRate });
        }
      }
      result[mp.code] = { minResult, maxResult };
    });
    return result;
  }, [achatsProduit, selectedAchatId, feesDetailsByMp, personnalisation, exchangeRates]);

  // OnClose personnalisé pour rafraîchir les règles si besoin
  const handleClose = () => {
    if (onRulesUpdated && selectedProduct?.id) {
      onRulesUpdated(selectedProduct.id);
    }
    if (onClose) onClose();
  };

  // Filtrage des marketplaces sans restriction pour ce produit
  const marketplacesSansRestriction = useMemo(() => {
    if (!selectedProduct) return [];
    return MARKETPLACES.filter(mp => {
      const restriction = restrictionsByProduct[selectedProduct.id]?.[mp.id];
      // Pas de restriction ou tableau de raisons vide
      return !restriction || !Array.isArray(restriction.reasons) || restriction.reasons.length === 0;
    });
  }, [selectedProduct, restrictionsByProduct]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>Gestion du repricer – {selectedProduct?.Titre}</DialogTitle>
      <DialogContent>
        {selectedProduct && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Achat associé (pour toutes les marketplaces)</Typography>
              <AchatSelector productId={selectedProduct.id} value={selectedAchatId} onChange={setSelectedAchatId} />
            </Box>
            <React.Fragment>
              {(achatsProduit.length === 0 || rules.length === 0) && open ? (
                <Box sx={{ p: 3 }}>
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 1 }} />
                  ))}
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Pays</TableCell>
                        <TableCell>État</TableCell>
                        <TableCell>Règle</TableCell>
                        <TableCell>Prix</TableCell>
                        <TableCell>Frais Amazon</TableCell>
                        <TableCell>TVA</TableCell>
                        <TableCell>Profit</TableCell>
                        <TableCell>ROI</TableCell>
                        <TableCell>Marge</TableCell>
                        <TableCell>Override</TableCell>
                        <TableCell>Actif</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {marketplacesSansRestriction.map(mp => {
                        const ruleId = repricingRuleData[selectedProduct.id]?.[mp.code]?.rule_id;
                        const ruleExists = rules.some(r => r.id === ruleId);
                        const selectValue = ruleExists ? ruleId : '';
                        const computed = computedByMp[mp.code] || {};
                        const minMax = minMaxComputedByMp[mp.code] || {};
                        return (
                        <React.Fragment key={mp.code}>
                          <TableRow key={mp.code}>
                            {/* Pays */}
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <img src={mp.flag} alt={mp.code} style={{ width: 20, height: 15, borderRadius: 2, border: '1px solid #eee' }} />
                                <Typography variant="body2">{mp.code}</Typography>
                              </Box>
                            </TableCell>
                            {/* État */}
                            <TableCell>
                              <Chip
                                label={!!repricerActif[selectedProduct.id]?.[mp.code] ? 'Actif' : 'Inactif'}
                                color={!!repricerActif[selectedProduct.id]?.[mp.code] ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            {/* Règle */}
                            <TableCell>
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={selectedRuleIds[selectedProduct.id]?.[mp.code] || (rules.find(r => r.is_default && (r.marketplace === mp.code || !r.marketplace))?.id ?? '')}
                                  onChange={e => setSelectedRuleIds(prev => ({
                                    ...prev,
                                    [selectedProduct.id]: {
                                      ...prev[selectedProduct.id],
                                      [mp.code]: e.target.value
                                    }
                                  }))}
                                >
                                  {rules.filter(r => r.marketplace === mp.code || !r.marketplace).map(rule => (
                                    <MenuItem key={rule.id} value={rule.id}>{rule.rule_name}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            {/* Prix */}
                            <TableCell>
                              {(() => {
                                const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currencyCode;
                                const price = getPrice(mp);
                                const { display, tooltip } = displayEurWithTooltip(price, currencyCode, exchangeRates);
                                return (
                                  <Tooltip title={tooltip} arrow>
                                    <span style={{ fontSize: '14px' }}>{display}</span>
                                  </Tooltip>
                                );
                              })()}
                            </TableCell>
                            {/* Frais Amazon */}
                            <TableCell>
                              {(() => {
                                const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currencyCode;
                                const amount = feesDetailsByMp[mp.code]?.Total !== undefined ? feesDetailsByMp[mp.code].Total : null;
                                let tooltip = '';
                                if (['SEK', 'PLN', 'GBP'].includes(currencyCode)) {
                                  tooltip = (
                                    <div style={{ fontSize: 13 }}>
                                      <div><b>Frais Amazon estimés</b></div>
                                      <div>FBA : {typeof feesDetailsByMp[mp.code]?.FBAFees === 'number' && !isNaN(feesDetailsByMp[mp.code].FBAFees) ? feesDetailsByMp[mp.code].FBAFees.toFixed(2) : '-'} {currencyCode}</div>
                                      <div>Referral : {typeof feesDetailsByMp[mp.code]?.ReferralFees === 'number' && !isNaN(feesDetailsByMp[mp.code].ReferralFees) ? feesDetailsByMp[mp.code].ReferralFees.toFixed(2) : '-'} {currencyCode}</div>
                                      <div>Digital : {typeof feesDetailsByMp[mp.code]?.DigitalFees === 'number' && !isNaN(feesDetailsByMp[mp.code].DigitalFees) ? feesDetailsByMp[mp.code].DigitalFees.toFixed(2) : '-'} {currencyCode}</div>
                                      <div style={{ marginTop: 6 }}><b>Total :</b> {typeof feesDetailsByMp[mp.code]?.Total === 'number' && !isNaN(feesDetailsByMp[mp.code].Total) ? feesDetailsByMp[mp.code].Total.toFixed(2) : '-'} {currencyCode}</div>
                                    </div>
                                  );
                                } else {
                                  tooltip = (
                                    <div style={{ fontSize: 13 }}>
                                      <div><b>Frais Amazon estimés</b></div>
                                      <div>FBA : {typeof feesDetailsByMp[mp.code]?.FBAFees === 'number' && !isNaN(feesDetailsByMp[mp.code].FBAFees) ? feesDetailsByMp[mp.code].FBAFees.toFixed(2) : '-'} {currencyCode}</div>
                                      <div>Referral : {typeof feesDetailsByMp[mp.code]?.ReferralFees === 'number' && !isNaN(feesDetailsByMp[mp.code].ReferralFees) ? feesDetailsByMp[mp.code].ReferralFees.toFixed(2) : '-'} {currencyCode}</div>
                                      <div>Digital : {typeof feesDetailsByMp[mp.code]?.DigitalFees === 'number' && !isNaN(feesDetailsByMp[mp.code].DigitalFees) ? feesDetailsByMp[mp.code].DigitalFees.toFixed(2) : '-'} {currencyCode}</div>
                                      <div style={{ marginTop: 6 }}><b>Total :</b> {typeof feesDetailsByMp[mp.code]?.Total === 'number' && !isNaN(feesDetailsByMp[mp.code].Total) ? feesDetailsByMp[mp.code].Total.toFixed(2) : '-'} {currencyCode}</div>
                                    </div>
                                  );
                                }
                                const { display } = displayEurWithTooltip(amount, currencyCode, exchangeRates);
                                return (
                                  <Tooltip title={tooltip} arrow>
                                    <span style={{ fontSize: '14px' }}>{!!repricerActif[selectedProduct.id]?.[mp.code] ? display : <span style={{ color: '#aaa' }}>-</span>}</span>
                                  </Tooltip>
                                );
                              })()}
                            </TableCell>
                            {/* TVA */}
                            <TableCell>
                              {(() => {
                                const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currencyCode;
                                let amount = computed.tvaDetails?.tvaNette;
                                let tooltip = '';
                                if (['SEK', 'PLN', 'GBP'].includes(currencyCode) && exchangeRates[currencyCode]) {
                                  const tvaCollectee = computed.tvaDetails?.tvaCollectee ?? 0;
                                  const tvaDeductibleEur = computed.tvaDetails?.tvaDeductible ?? 0;
                                  const tvaDeductibleLocal = tvaDeductibleEur / exchangeRates[currencyCode];
                                  const tvaNetteLocal = tvaCollectee - tvaDeductibleLocal;
                                  amount = tvaNetteLocal * exchangeRates[currencyCode]; // conversion en EUR pour l'affichage colonne
                                  tooltip = (
                                    <div>
                                      <div><b>TVA collectée :</b> {typeof tvaCollectee === 'number' && !isNaN(tvaCollectee) ? tvaCollectee.toFixed(2) : '-'} {currencyCode} (taux {typeof computed.tvaDetails?.txTVACollecte === 'number' && !isNaN(computed.tvaDetails?.txTVACollecte) ? computed.tvaDetails?.txTVACollecte.toFixed(2) : '-'}%)</div>
                                      <div><b>TVA déductible :</b> {typeof tvaDeductibleLocal === 'number' && !isNaN(tvaDeductibleLocal) ? tvaDeductibleLocal.toFixed(2) : '-'} {currencyCode} {computed.tvaDeductible ? `(taux ${typeof computed.tvaDetails?.txTVADeductible === 'number' && !isNaN(computed.tvaDetails?.txTVADeductible) ? computed.tvaDetails?.txTVADeductible.toFixed(2) : '-'}%)` : '(non déductible)'}</div>
                                      <div><b>TVA nette :</b> {typeof tvaNetteLocal === 'number' && !isNaN(tvaNetteLocal) ? tvaNetteLocal.toFixed(2) : '-'} {currencyCode}</div>
                                    </div>
                                  );
                                  return (
                                    <Tooltip title={tooltip} arrow>
                                      <span style={{ fontSize: '14px' }}>{!!repricerActif[selectedProduct.id]?.[mp.code] ? amount.toFixed(2) + ' EUR' : <span style={{ color: '#aaa' }}>-</span>}</span>
                                    </Tooltip>
                                  );
                                } else {
                                  const { display } = displayEurWithTooltip(amount, currencyCode, exchangeRates);
                                  tooltip = (
                                    <div>
                                      <div><b>TVA collectée :</b> {typeof computed.tvaDetails?.tvaCollectee === 'number' && !isNaN(computed.tvaDetails?.tvaCollectee) ? computed.tvaDetails?.tvaCollectee.toFixed(2) : '-'} {currencyCode} (taux {typeof computed.tvaDetails?.txTVACollecte === 'number' && !isNaN(computed.tvaDetails?.txTVACollecte) ? computed.tvaDetails?.txTVACollecte.toFixed(2) : '-'}%)</div>
                                      <div><b>TVA déductible :</b> {typeof computed.tvaDetails?.tvaDeductible === 'number' && !isNaN(computed.tvaDetails?.tvaDeductible) ? computed.tvaDetails?.tvaDeductible.toFixed(2) : '-'} {currencyCode} {computed.tvaDeductible ? `(taux ${typeof computed.tvaDetails?.txTVADeductible === 'number' && !isNaN(computed.tvaDetails?.txTVADeductible) ? computed.tvaDetails?.txTVADeductible.toFixed(2) : '-'}%)` : '(non déductible)'}</div>
                                    </div>
                                  );
                                  return (
                                    <Tooltip title={tooltip} arrow>
                                      <span style={{ fontSize: '14px' }}>{!!repricerActif[selectedProduct.id]?.[mp.code] ? display : <span style={{ color: '#aaa' }}>-</span>}</span>
                                    </Tooltip>
                                  );
                                }
                              })()}
                            </TableCell>
                            {/* Profit */}
                            <TableCell>
                              {(() => {
                                const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currencyCode;
                                let amount = computed.profit;
                                let tooltip = '';
                                if (["SEK", "PLN", "GBP"].includes(currencyCode) && exchangeRates[currencyCode]) {
                                  const taux = exchangeRates[currencyCode];
                                  const prixVenteEur = computed.price * taux;
                                  const fraisAmazonEur = computed.fraisAmazon * taux;
                                  // On récupère la TVA nette déjà convertie comme dans la colonne TVA
                                  let tvaNetteEur;
                                  const tvaCollectee = computed.tvaDetails?.tvaCollectee ?? 0;
                                  const tvaDeductibleEur = computed.tvaDetails?.tvaDeductible ?? 0;
                                  const tvaDeductibleLocal = tvaDeductibleEur / taux;
                                  const tvaNetteLocal = tvaCollectee - tvaDeductibleLocal;
                                  tvaNetteEur = tvaNetteLocal * taux;
                                  const profitEur = prixVenteEur
                                    - computed.prixAchat
                                    - fraisAmazonEur
                                    - computed.fraisPreparation
                                    - computed.fraisLivraison
                                    - tvaNetteEur;
                                  amount = profitEur;
                                  // Tooltip : afficher le profit en devise locale
                                  const profitLocal = amount / taux;
                                  tooltip = `Profit : ${typeof profitLocal === 'number' && !isNaN(profitLocal) ? profitLocal.toFixed(2) : '-'} ${currencyCode}`;

                                  return (
                                    <Tooltip title={tooltip} arrow>
                                      <span style={{ fontSize: '14px' }}>{!!repricerActif[selectedProduct.id]?.[mp.code] ? amount.toFixed(2) + ' EUR' : <span style={{ color: '#aaa' }}>-</span>}</span>
                                    </Tooltip>
                                  );
                                } else {
                                  const { display, tooltip: tip } = displayEurWithTooltip(amount, currencyCode, exchangeRates);
                                  tooltip = tip || '';
                                  return (
                                    <span style={{ fontSize: '14px' }}>{!!repricerActif[selectedProduct.id]?.[mp.code] ? display : <span style={{ color: '#aaa' }}>-</span>}</span>
                                  );
                                }
                              })()}
                            </TableCell>
                            {/* ROI */}
                            <TableCell>
                              <Typography variant="body2" style={{ fontSize: '14px' }}>
                                {!!repricerActif[selectedProduct.id]?.[mp.code] ? (
                                  (() => {
                                    const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currencyCode;
                                    let amount = computed.profit;
                                    if (["SEK", "PLN", "GBP"].includes(currencyCode) && exchangeRates[currencyCode]) {
                                      const taux = exchangeRates[currencyCode];
                                      const prixVenteEur = computed.price * taux;
                                      const fraisAmazonEur = computed.fraisAmazon * taux;
                                      let tvaNetteEur;
                                      const tvaCollectee = computed.tvaDetails?.tvaCollectee ?? 0;
                                      const tvaDeductibleEur = computed.tvaDetails?.tvaDeductible ?? 0;
                                      const tvaDeductibleLocal = tvaDeductibleEur / taux;
                                      const tvaNetteLocal = tvaCollectee - tvaDeductibleLocal;
                                      tvaNetteEur = tvaNetteLocal * taux;
                                      const profitEur = prixVenteEur
                                        - computed.prixAchat
                                        - fraisAmazonEur
                                        - computed.fraisPreparation
                                        - computed.fraisLivraison
                                        - tvaNetteEur;
                                      amount = profitEur;
                                      const coutTotal = computed.prixAchat + computed.fraisPreparation + computed.fraisLivraison;
                                      return typeof amount === 'number' && !isNaN(amount) && coutTotal !== 0 ? (amount / coutTotal * 100).toFixed(2) + ' %' : '-';
                                    } else {
                                      return typeof computed.roi === 'number' && !isNaN(computed.roi) ? computed.roi.toFixed(2) + ' %' : '-';
                                    }
                                  })()
                                ) : (
                                  <span style={{ color: '#aaa' }}>-</span>
                                )}
                              </Typography>
                            </TableCell>
                            {/* Marge */}
                            <TableCell>
                              <Typography variant="body2" style={{ fontSize: '14px' }}>
                                {!!repricerActif[selectedProduct.id]?.[mp.code] ? (
                                  (() => {
                                    const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currencyCode;
                                    let amount = computed.profit;
                                    if (["SEK", "PLN", "GBP"].includes(currencyCode) && exchangeRates[currencyCode]) {
                                      const taux = exchangeRates[currencyCode];
                                      const prixVenteEur = computed.price * taux;
                                      const fraisAmazonEur = computed.fraisAmazon * taux;
                                      let tvaNetteEur;
                                      const tvaCollectee = computed.tvaDetails?.tvaCollectee ?? 0;
                                      const tvaDeductibleEur = computed.tvaDetails?.tvaDeductible ?? 0;
                                      const tvaDeductibleLocal = tvaDeductibleEur / taux;
                                      const tvaNetteLocal = tvaCollectee - tvaDeductibleLocal;
                                      tvaNetteEur = tvaNetteLocal * taux;
                                      const profitEur = prixVenteEur
                                        - computed.prixAchat
                                        - fraisAmazonEur
                                        - computed.fraisPreparation
                                        - computed.fraisLivraison
                                        - tvaNetteEur;
                                      amount = profitEur;
                                      return typeof amount === 'number' && !isNaN(amount) && computed.price !== 0 ? (amount / (computed.price * taux) * 100).toFixed(2) + ' %' : '-';
                                    } else {
                                      return typeof computed.margin === 'number' && !isNaN(computed.margin) ? computed.margin.toFixed(2) + ' %' : '-';
                                    }
                                  })()
                                ) : (
                                  <span style={{ color: '#aaa' }}>-</span>
                                )}
                              </Typography>
                            </TableCell>
                            {/* Override */}
                            <TableCell>
                              {openPersonnalisation[mp.code] ? (
                                <IconButton size="small" onClick={async () => {
                                  // Fermer la personnalisation
                                  setOpenPersonnalisation(prev => ({ ...prev, [mp.code]: false }));
                                  // Réinitialiser les valeurs min/max pour cette marketplace
                                  setPersonnalisation(prev => ({
                                    ...prev,
                                    [mp.code]: { minType: '', minValue: '', maxType: '', maxValue: '' }
                                  }));
                                  // Mettre à jour la BDD pour effacer les overrides
                                  if (selectedProduct && repricingRuleData[selectedProduct.id]?.[mp.code]) {
                                    const rule = repricingRuleData[selectedProduct.id][mp.code];
                                    const payload = [{
                                      ...rule,
                                      min_profit_override: null,
                                      max_profit_override: null,
                                      min_margin_override: null,
                                      max_margin_override: null,
                                      min_roi_override: null,
                                      max_roi_override: null,
                                    }];
                                    await fetch('/api/repricing-rules/save-bulk', {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ rules: payload })
                                    });
                                    // Optionnel : rafraîchir les règles côté parent
                                    if (onRulesUpdated && selectedProduct?.id) {
                                      await onRulesUpdated(selectedProduct.id);
                                    }
                                  }
                                }} title="Fermer la personnalisation">
                                  <X size={18} />
                                </IconButton>
                              ) : (
                                <IconButton size="small" onClick={() => setOpenPersonnalisation(prev => ({ ...prev, [mp.code]: true }))} title="Personnaliser">
                                  <Settings size={18} />
                                </IconButton>
                              )}
                            </TableCell>
                            {/* Actif */}
                            <TableCell>
                              <Switch
                                checked={!!repricerActif[selectedProduct.id]?.[mp.code]}
                                onChange={() => handleToggleRepricer(mp)}
                              />
                            </TableCell>
                          </TableRow>
                          {/* Ligne de personnalisation si ouverte */}
                          {openPersonnalisation[mp.code] && (
                            <>
                              {/* Ligne Min */}
                              <TableRow>
                                <TableCell colSpan={3} sx={{ bgcolor: '#f5f7fa', p: 1 }}>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Select
                                      size="small"
                                      value={personnalisation[mp.code]?.minType || 'profit'}
                                      onChange={e => setPersonnalisation(prev => ({
                                        ...prev,
                                        [mp.code]: { ...prev[mp.code], minType: e.target.value }
                                      }))}
                                    >
                                      <MenuItem value="profit">Profit (€)</MenuItem>
                                      <MenuItem value="marge">Marge (%)</MenuItem>
                                      <MenuItem value="roi">ROI (%)</MenuItem>
                                    </Select>
                                    <TextField
                                      label="Min"
                                      type="number"
                                      size="small"
                                      value={personnalisation[mp.code]?.minValue || ''}
                                      onChange={e => setPersonnalisation(prev => ({
                                        ...prev,
                                        [mp.code]: {
                                          ...prev[mp.code],
                                          minValue: e.target.value,
                                          minType: prev[mp.code]?.minType || 'profit'
                                        }
                                      }))}
                                      sx={{ width: 90 }}
                                    />
                                  </Box>
                                </TableCell>
                                {/* Colonne Prix Min */}
                                <TableCell sx={{ fontWeight: 600, color: 'success.main', textAlign: 'center' }}>
                                  {minMax.minResult && typeof minMax.minResult.price === 'number' && !isNaN(minMax.minResult.price) ? (
                                    (() => {
                                      const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currency;
                                      return <span style={{ fontSize: '14px' }}>{minMax.minResult.price.toFixed(2)} {currencyCode}</span>;
                                    })()
                                  ) : (
                                    <Tooltip title="Impossible d'atteindre la contrainte avec les coûts/frais actuels" arrow>
                                      <span style={{ color: '#ff9800', fontWeight: 600 }}>-</span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                                {/* Colonne Frais Amazon Min */}
                                <TableCell sx={{ color: 'primary.main', textAlign: 'left' }}>
                                  {minMax.minResult && minMax.minResult.fees && typeof minMax.minResult.fees.total === 'number' && !isNaN(minMax.minResult.fees.total) ? (
                                    (() => {
                                      const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currency;
                                      const fees = minMax.minResult.fees;
                                      const tooltip = (
                                        <div style={{ fontSize: 13 }}>
                                          <div><b>Frais Amazon estimés</b></div>
                                          <div>FBA : {typeof fees.fba === 'number' && !isNaN(fees.fba) ? fees.fba.toFixed(2) : '-'} {currencyCode}</div>
                                          <div>Referral : {typeof fees.referral === 'number' && !isNaN(fees.referral) ? fees.referral.toFixed(2) : '-'} {currencyCode}</div>
                                          <div>Digital : {typeof fees.digital === 'number' && !isNaN(fees.digital) ? fees.digital.toFixed(2) : '-'} {currencyCode}</div>
                                          <div style={{ marginTop: 6 }}><b>Total :</b> {fees.total.toFixed(2)} {currencyCode}</div>
                                        </div>
                                      );
                                      let display = fees.total.toFixed(2);
                                      if (["SEK", "PLN", "GBP"].includes(currencyCode) && exchangeRates[currencyCode]) {
                                        display = (fees.total * exchangeRates[currencyCode]).toFixed(2) + ' EUR';
                                      } else {
                                        display = fees.total.toFixed(2) + ' ' + currencyCode;
                                      }
                                      return (
                                        <Tooltip title={tooltip} arrow>
                                          <span style={{ fontSize: '14px' }}>{display}</span>
                                        </Tooltip>
                                      );
                                    })()
                                  ) : <span style={{ color: '#aaa' }}>-</span>}
                                </TableCell>
                                {/* Colonne TVA Min */}
                                <TableCell sx={{ color: 'warning.main', textAlign: 'center' }}>
                                  {minMax.minResult && minMax.minResult.vat && typeof minMax.minResult.vat.net === 'number' && !isNaN(minMax.minResult.vat.net) ? (
                                    (() => {
                                      const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currency;
                                      const vat = minMax.minResult.vat;
                                      const tooltip = (
                                        <div style={{ fontSize: 13 }}>
                                          <div><b>TVA collectée :</b> {typeof vat.collected === 'number' && !isNaN(vat.collected) ? vat.collected.toFixed(2) : '-'} {currencyCode}</div>
                                          <div><b>TVA déductible :</b> {typeof vat.deductible === 'number' && !isNaN(vat.deductible) ? vat.deductible.toFixed(2) : '-'} {currencyCode}</div>
                                          <div><b>TVA nette :</b> {vat.net.toFixed(2)} {currencyCode}</div>
                                        </div>
                                      );
                                      let display = vat.net.toFixed(2);
                                      if (["SEK", "PLN", "GBP"].includes(currencyCode) && exchangeRates[currencyCode]) {
                                        display = (vat.net * exchangeRates[currencyCode]).toFixed(2) + ' EUR';
                                      } else {
                                        display = vat.net.toFixed(2) + ' ' + currencyCode;
                                      }
                                      return (
                                        <Tooltip title={tooltip} arrow>
                                          <span style={{ fontSize: '14px' }}>{display}</span>
                                        </Tooltip>
                                      );
                                    })()
                                  ) : <span style={{ color: '#aaa' }}>-</span>}
                                </TableCell>
                                {/* Colonne Profit Min */}
                                <TableCell sx={{ color: 'success.main', textAlign: 'center' }}>
                                  {minMax.minResult && typeof minMax.minResult.profit === 'number' && !isNaN(minMax.minResult.profit) ? (
                                    (() => {
                                      const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currency;
                                      if (["SEK", "PLN", "GBP"].includes(currencyCode) && exchangeRates[currencyCode]) {
                                        const profitEur = minMax.minResult.profit * exchangeRates[currencyCode];
                                        const profitLocal = minMax.minResult.profit;
                                        return (
                                          <Tooltip title={`${profitLocal.toFixed(2)} ${currencyCode}`} arrow>
                                            <span style={{ fontSize: '14px' }}>{profitEur.toFixed(2)} EUR</span>
                                          </Tooltip>
                                        );
                                      } else {
                                        return <span style={{ fontSize: '14px' }}>{minMax.minResult.profit.toFixed(2)} {currencyCode}</span>;
                                      }
                                    })()
                                  ) : <span style={{ color: '#aaa' }}>-</span>}
                                </TableCell>
                                {/* Colonne ROI Min */}
                                <TableCell sx={{ color: 'secondary.main', textAlign: 'center' }}>
                                  {minMax.minResult && typeof minMax.minResult.roi === 'number' && !isNaN(minMax.minResult.roi) ? (
                                    <span style={{ fontSize: '14px' }}>{minMax.minResult.roi.toFixed(2)}%</span>
                                  ) : <span style={{ color: '#aaa' }}>-</span>}
                                </TableCell>
                                {/* Colonne Marge Min */}
                                <TableCell sx={{ color: 'info.main', textAlign: 'center' }}>
                                  {minMax.minResult && typeof minMax.minResult.profit === 'number' && typeof minMax.minResult.price === 'number' && minMax.minResult.price !== 0 && !isNaN(minMax.minResult.profit) && !isNaN(minMax.minResult.price) ? (
                                    <span style={{ fontSize: '14px' }}>{((minMax.minResult.profit / minMax.minResult.price) * 100).toFixed(2)}%</span>
                                  ) : <span style={{ color: '#aaa' }}>-</span>}
                                </TableCell>

                              </TableRow>
                              {/* Ligne Max */}
                              <TableRow>
                                <TableCell colSpan={3} sx={{ bgcolor: '#f5f7fa', p: 1 }}>
                                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Select
                                      size="small"
                                      value={personnalisation[mp.code]?.maxType || 'profit'}
                                      onChange={e => setPersonnalisation(prev => ({
                                        ...prev,
                                        [mp.code]: { ...prev[mp.code], maxType: e.target.value }
                                      }))}
                                    >
                                      <MenuItem value="profit">Profit (€)</MenuItem>
                                      <MenuItem value="marge">Marge (%)</MenuItem>
                                      <MenuItem value="roi">ROI (%)</MenuItem>
                                    </Select>
                                    <TextField
                                      label="Max"
                                      type="number"
                                      size="small"
                                      value={personnalisation[mp.code]?.maxValue || ''}
                                      onChange={e => setPersonnalisation(prev => ({
                                        ...prev,
                                        [mp.code]: {
                                          ...prev[mp.code],
                                          maxValue: e.target.value,
                                          maxType: prev[mp.code]?.maxType || 'profit'
                                        }
                                      }))}
                                      sx={{ width: 90 }}
                                    />
                                  </Box>
                                </TableCell>
                                {/* Colonne Prix Max */}
                                <TableCell sx={{ fontWeight: 600, color: 'success.main', textAlign: 'center' }}>
                                  {minMax.maxResult && typeof minMax.maxResult.price === 'number' && !isNaN(minMax.maxResult.price) ? (
                                    (() => {
                                      const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currency;
                                      if (["SEK", "PLN", "GBP"].includes(currencyCode) && exchangeRates[currencyCode]) {
                                        const priceEur = minMax.maxResult.price * exchangeRates[currencyCode];
                                        const priceLocal = minMax.maxResult.price;
                                        return (
                                          <Tooltip title={`${priceLocal.toFixed(2)} ${currencyCode}`} arrow>
                                            <span style={{ fontSize: '14px' }}>{priceEur.toFixed(2)} EUR</span>
                                          </Tooltip>
                                        );
                                      } else {
                                        return <span style={{ fontSize: '14px' }}>{minMax.maxResult.price.toFixed(2)} {currencyCode}</span>;
                                      }
                                    })()
                                  ) : <span style={{ color: '#aaa' }}>-</span>}
                                </TableCell>
                                {/* Colonne Frais Amazon Max */}
                                <TableCell sx={{ color: 'primary.main', textAlign: 'left' }}>
                                  {minMax.maxResult && minMax.maxResult.fees && typeof minMax.maxResult.fees.total === 'number' && !isNaN(minMax.maxResult.fees.total) ? (
                                    (() => {
                                      const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currency;
                                      const fees = minMax.maxResult.fees;
                                      const tooltip = (
                                        <div style={{ fontSize: 13 }}>
                                          <div><b>Frais Amazon estimés</b></div>
                                          <div>FBA : {typeof fees.fba === 'number' && !isNaN(fees.fba) ? fees.fba.toFixed(2) : '-'} {currencyCode}</div>
                                          <div>Referral : {typeof fees.referral === 'number' && !isNaN(fees.referral) ? fees.referral.toFixed(2) : '-'} {currencyCode}</div>
                                          <div>Digital : {typeof fees.digital === 'number' && !isNaN(fees.digital) ? fees.digital.toFixed(2) : '-'} {currencyCode}</div>
                                          <div style={{ marginTop: 6 }}><b>Total :</b> {fees.total.toFixed(2)} {currencyCode}</div>
                                        </div>
                                      );
                                      let display = fees.total.toFixed(2);
                                      if (["SEK", "PLN", "GBP"].includes(currencyCode) && exchangeRates[currencyCode]) {
                                        display = (fees.total * exchangeRates[currencyCode]).toFixed(2) + ' EUR';
                                      } else {
                                        display = fees.total.toFixed(2) + ' ' + currencyCode;
                                      }
                                      return (
                                        <Tooltip title={tooltip} arrow>
                                          <span style={{ fontSize: '14px' }}>{display}</span>
                                        </Tooltip>
                                      );
                                    })()
                                  ) : <span style={{ color: '#aaa' }}>-</span>}
                                </TableCell>
                                {/* Colonne TVA Max */}
                                <TableCell sx={{ color: 'warning.main', textAlign: 'center' }}>
                                  {minMax.maxResult && minMax.maxResult.vat && typeof minMax.maxResult.vat.net === 'number' && !isNaN(minMax.maxResult.vat.net) ? (
                                    (() => {
                                      const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currency;
                                      const vat = minMax.maxResult.vat;
                                      const tooltip = (
                                        <div style={{ fontSize: 13 }}>
                                          <div><b>TVA collectée :</b> {typeof vat.collected === 'number' && !isNaN(vat.collected) ? vat.collected.toFixed(2) : '-'} {currencyCode}</div>
                                          <div><b>TVA déductible :</b> {typeof vat.deductible === 'number' && !isNaN(vat.deductible) ? vat.deductible.toFixed(2) : '-'} {currencyCode}</div>
                                          <div><b>TVA nette :</b> {vat.net.toFixed(2)} {currencyCode}</div>
                                        </div>
                                      );
                                      let display = vat.net.toFixed(2);
                                      if (["SEK", "PLN", "GBP"].includes(currencyCode) && exchangeRates[currencyCode]) {
                                        display = (vat.net * exchangeRates[currencyCode]).toFixed(2) + ' EUR';
                                      } else {
                                        display = vat.net.toFixed(2) + ' ' + currencyCode;
                                      }
                                      return (
                                        <Tooltip title={tooltip} arrow>
                                          <span style={{ fontSize: '14px' }}>{display}</span>
                                        </Tooltip>
                                      );
                                    })()
                                  ) : <span style={{ color: '#aaa' }}>-</span>}
                                </TableCell>
                                {/* Colonne Profit Max */}
                                <TableCell sx={{ color: 'success.main', textAlign: 'center' }}>
                                  {minMax.maxResult && typeof minMax.maxResult.profit === 'number' && !isNaN(minMax.maxResult.profit) ? (
                                    (() => {
                                      const currencyCode = repricingRuleData[selectedProduct.id]?.[mp.code]?.currency || mp.currency;
                                      if (["SEK", "PLN", "GBP"].includes(currencyCode) && exchangeRates[currencyCode]) {
                                        const profitEur = minMax.maxResult.profit * exchangeRates[currencyCode];
                                        const profitLocal = minMax.maxResult.profit;
                                        return (
                                          <Tooltip title={`${profitLocal.toFixed(2)} ${currencyCode}`} arrow>
                                            <span style={{ fontSize: '14px' }}>{profitEur.toFixed(2)} EUR</span>
                                          </Tooltip>
                                        );
                                      } else {
                                        return <span style={{ fontSize: '14px' }}>{minMax.maxResult.profit.toFixed(2)} {currencyCode}</span>;
                                      }
                                    })()
                                  ) : <span style={{ color: '#aaa' }}>-</span>}
                                </TableCell>
                                 {/* Colonne ROI Max */}
                                <TableCell sx={{ color: 'secondary.main', textAlign: 'center' }}>
                                  {minMax.maxResult && typeof minMax.maxResult.roi === 'number' && !isNaN(minMax.maxResult.roi) ? (
                                    <span style={{ fontSize: '14px' }}>{minMax.maxResult.roi.toFixed(2)}%</span>
                                  ) : <span style={{ color: '#aaa' }}>-</span>}

                                </TableCell>
                                {/* Colonne Marge Max */}
                                <TableCell sx={{ color: 'info.main', textAlign: 'center' }}>
                                  {minMax.maxResult && typeof minMax.maxResult.profit === 'number' && typeof minMax.maxResult.price === 'number' && minMax.maxResult.price !== 0 && !isNaN(minMax.maxResult.profit) && !isNaN(minMax.maxResult.price) ? (
                                    <span style={{ fontSize: '14px' }}>{((minMax.maxResult.profit / minMax.maxResult.price) * 100).toFixed(2)}%</span>
                                  ) : <span style={{ color: '#aaa' }}>-</span>}
                                </TableCell>                  
                              </TableRow>
                            </>
                          )}
                        </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </React.Fragment>
          </>
        )}
        {/* Menu de sélection de règle */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseRuleMenu}>
          {rules.map(rule => (
            <MenuItem key={rule.id} onClick={() => handleSelectRule(rule)}>{rule.rule_name}</MenuItem>
          ))}
        </Menu>
        {/* Modale d'override */}
        <Dialog open={overrideDialog.open} onClose={handleCloseOverride} maxWidth="xs" fullWidth>
          <DialogTitle>Override – {overrideDialog.country}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Prix" value={overrideForm.price} onChange={e => handleOverrideChange('price', e.target.value)} type="number" fullWidth />
              <TextField label="Profit min (€)" value={overrideForm.minProfit} onChange={e => handleOverrideChange('minProfit', e.target.value)} type="number" fullWidth />
              <TextField label="Marge min (%)" value={overrideForm.minMargin} onChange={e => handleOverrideChange('minMargin', e.target.value)} type="number" fullWidth />
              <TextField label="ROI min (%)" value={overrideForm.minROI} onChange={e => handleOverrideChange('minROI', e.target.value)} type="number" fullWidth />
              <TextField label="Profit max (€)" value={overrideForm.maxProfit} onChange={e => handleOverrideChange('maxProfit', e.target.value)} type="number" fullWidth />
              <TextField label="Marge max (%)" value={overrideForm.maxMargin} onChange={e => handleOverrideChange('maxMargin', e.target.value)} type="number" fullWidth />
              <TextField label="ROI max (%)" value={overrideForm.maxROI} onChange={e => handleOverrideChange('maxROI', e.target.value)} type="number" fullWidth />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseOverride}>Annuler</Button>
            <Button onClick={handleSaveOverride} variant="contained">Enregistrer</Button>
          </DialogActions>
        </Dialog>
        {/* Snackbar local (affiché seulement si showSnackbar n'est pas fourni) */}
        {!showSnackbar && (
          <Snackbar
            open={snackbar.open}
            autoHideDuration={3000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Fermer</Button>
        <Button variant="contained" color="primary" onClick={handleSaveRepricer} disabled={saving}>Sauvegarder</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogRepricer; 